import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  FaUserShield,
  FaUserTie,
  FaCompass,
  FaUsersCog,
  FaMicrochip,
  FaBookOpen,
  FaGlobeAsia,
  FaRupeeSign,
  FaBullhorn,
  FaBed,
  FaShuttleVan,
  FaUtensils,
  FaClipboardList,
  FaIndustry,
  FaHandshake,
  FaUniversity,
} from 'react-icons/fa';
import { Container, Section, Reveal, Kicker, DotLattice, CopperGlow } from '../../components/ui';
import { COMMITTEES, COMMITTEE_PHOTOS, CONFERENCE } from '../../data/conference';

const slugify = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const COMMITTEE_ICONS = [
  <FaUserShield />,
  <FaUserTie />,
  <FaCompass />,
  <FaUsersCog />,
  <FaMicrochip />,
  <FaBookOpen />,
  <FaGlobeAsia />,
  <FaRupeeSign />,
  <FaBullhorn />,
  <FaBed />,
  <FaShuttleVan />,
  <FaUtensils />,
  <FaClipboardList />,
  <FaIndustry />,
  <FaHandshake />,
];

/* ============================ HERO ============================ */

const HeroWrap = styled.header`
  padding: clamp(8rem, 14vw, 11rem) 0 clamp(3rem, 6vw, 4.5rem);
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid var(--line);
`;

/* copper wireframe "structure" — drawn in SVG so it reads crisply on the light hero */
const InfraArt = styled.svg`
  position: absolute;
  right: clamp(1.5rem, 5vw, 4.5rem);
  top: clamp(6rem, 12vw, 9.5rem);
  width: clamp(280px, 36vw, 540px);
  height: auto;
  pointer-events: none;
  user-select: none;
  overflow: visible;
  filter: drop-shadow(0 8px 22px rgba(201, 117, 26, 0.18));

  .frame {
    fill: none;
    stroke: var(--copper);
    stroke-width: 1.1;
    vector-effect: non-scaling-stroke;
  }

  .frame.soft {
    stroke: rgba(201, 117, 26, 0.4);
  }

  .frame.faint {
    stroke: rgba(34, 31, 26, 0.16);
  }

  .node {
    fill: var(--copper-bright);
  }

  @media (max-width: 860px) {
    opacity: 0.5;
  }

  @media (max-width: 600px) {
    display: none;
  }
`;

/* isometric building/circuit lattice rendered with copper strokes */
function StructureArt() {
  return (
    <InfraArt viewBox="0 0 360 240" aria-hidden="true">
      {/* ground grid (faint) */}
      <path className="frame faint" d="M20 196 L188 132 L340 188 L172 232 Z" />
      <path className="frame faint" d="M64 180 L210 124" />
      <path className="frame faint" d="M120 214 L268 156" />
      {/* main tower */}
      <path className="frame" d="M150 60 L236 92 L236 168 L150 140 Z" />
      <path className="frame" d="M150 60 L150 140 L96 162 L96 84 Z" />
      <path className="frame soft" d="M150 60 L96 84 L182 112 L236 92 Z" />
      {/* floor divisions */}
      <path className="frame soft" d="M96 110 L150 88 L236 118" />
      <path className="frame soft" d="M96 136 L150 114 L236 144" />
      {/* secondary block */}
      <path className="frame" d="M236 120 L300 142 L300 196 L236 176 Z" />
      <path className="frame soft" d="M236 148 L300 168" />
      {/* riser lines */}
      <path className="frame soft" d="M182 112 L182 188" />
      {/* nodes */}
      <circle className="node" cx="150" cy="60" r="3.2" />
      <circle className="node" cx="236" cy="92" r="2.6" />
      <circle className="node" cx="300" cy="142" r="2.6" />
      <circle className="node" cx="96" cy="84" r="2.2" />
    </InfraArt>
  );
}

const HeroTitle = styled.h1`
  font-size: clamp(2.6rem, 6.5vw, 4.75rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  max-width: 14ch;
  position: relative;

  em {
    font-style: normal;
    background: linear-gradient(115deg, var(--copper-bright) 10%, var(--copper) 55%, var(--copper-deep) 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const HeroLead = styled.p`
  color: var(--ink-dim);
  font-size: clamp(1rem, 1.5vw, 1.2rem);
  max-width: 44ch;
  margin-top: 1.25rem;
  line-height: 1.7;
  position: relative;
`;

const HeroRule = styled.div`
  margin-top: 2.2rem;
  width: min(300px, 50%);
  height: 3px;
  background: linear-gradient(90deg, var(--copper-bright), var(--copper) 55%, transparent);
  box-shadow: 0 0 14px rgba(224, 141, 60, 0.55);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: -54px;
    top: 0;
    width: 40px;
    height: 3px;
    background: repeating-linear-gradient(90deg, var(--copper) 0 7px, transparent 7px 12px);
    opacity: 0.7;
  }
`;

/* ====================== SCROLL-SPY CHIP NAV ===================== */

const JumpNav = styled.nav`
  position: sticky;
  top: 76px;
  z-index: 10;
  background: rgba(236, 233, 225, 0.9);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid var(--line);
  padding: 0.85rem 0;

  .rail {
    display: flex;
    gap: 0.55rem;
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 2px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Chip = styled.a`
  --cut: 9px;
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-family: var(--font-mono);
  font-size: 0.64rem;
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${(p) => (p.$active ? 'var(--copper-bright)' : 'var(--ink-dim)')};
  padding: 0.6rem 1.05rem;
  background: ${(p) =>
    p.$active
      ? 'linear-gradient(110deg, rgba(242, 176, 94, 0.85), rgba(120, 64, 18, 0.9) 45%, rgba(242, 176, 94, 0.7))'
      : 'rgba(58, 62, 70, 0.55)'};
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - var(--cut)) 0,
    100% 50%,
    calc(100% - var(--cut)) 100%,
    var(--cut) 100%,
    0 50%
  );
  transition: color 0.2s, filter 0.25s var(--ease-out);

  &::before {
    content: '';
    position: absolute;
    inset: 1.5px;
    z-index: -1;
    background: ${(p) =>
      p.$active
        ? 'linear-gradient(180deg, rgba(50, 30, 12, 0.96), rgba(18, 13, 8, 0.98))'
        : 'linear-gradient(180deg, #f4f2ec, #ffffff)'};
    clip-path: inherit;
  }

  svg {
    font-size: 0.85rem;
    color: ${(p) => (p.$active ? 'var(--copper-bright)' : 'var(--ink-faint)')};
    flex: none;
  }

  &:hover {
    color: var(--copper-bright);
    filter: brightness(1.15);

    svg {
      color: var(--copper-bright);
    }
  }
`;

/* ========================= GROUP LAYOUT ======================== */

const Group = styled.section`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: clamp(1.5rem, 4vw, 3.5rem);
  padding: clamp(2.2rem, 4.5vw, 3.2rem) 0;
  border-bottom: 1px solid var(--line);
  scroll-margin-top: 150px;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    gap: 1.2rem;
  }
`;

const GroupTitle = styled.div`
  position: sticky;
  top: 150px;
  align-self: start;

  .idx {
    font-family: var(--font-mono);
    font-size: clamp(1.1rem, 2vw, 1.5rem);
    font-weight: 600;
    letter-spacing: 0.08em;
    color: var(--copper-bright);
    display: block;
    margin-bottom: 0.4rem;
  }

  h2 {
    font-size: clamp(1.3rem, 2.3vw, 1.7rem);
    font-weight: 700;
    line-height: 1.2;
    max-width: 13ch;
    margin-bottom: 1.1rem;
  }

  .tick {
    width: 44px;
    height: 2px;
    background: var(--copper);
    box-shadow: 0 0 10px rgba(224, 141, 60, 0.6);
    margin-bottom: 1.2rem;
  }

  .dots {
    width: 110px;
    height: 56px;
    background-image: radial-gradient(rgba(34, 31, 26, 0.16) 1px, transparent 1px);
    background-size: 13px 13px;
  }

  @media (max-width: 860px) {
    position: static;

    h2 {
      max-width: none;
    }

    .dots {
      display: none;
    }
  }
`;

const MemberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 1180px) and (min-width: 861px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/* ======================== MEMBER CARD ========================= */

const MemberCard = styled.article`
  --cut: 16px;
  position: relative;
  isolation: isolate;
  display: flex;
  gap: 1.2rem;
  align-items: center;
  padding: 1.1rem 1.3rem;
  background: linear-gradient(
    110deg,
    rgba(126, 132, 138, 0.4) 0%,
    rgba(44, 49, 56, 0.9) 18%,
    rgba(224, 141, 60, 0.45) 50%,
    rgba(42, 47, 54, 0.92) 82%,
    rgba(126, 132, 138, 0.4) 100%
  );
  clip-path: polygon(
    var(--cut) 0,
    calc(100% - 8px) 0,
    100% 8px,
    100% calc(100% - var(--cut)),
    calc(100% - var(--cut)) 100%,
    8px 100%,
    0 calc(100% - 8px),
    0 var(--cut)
  );
  transition: transform 0.28s var(--ease-out), filter 0.28s var(--ease-out);

  &::before {
    content: '';
    position: absolute;
    inset: 1.5px;
    z-index: -1;
    background: radial-gradient(130% 160% at 90% -30%, rgba(224, 141, 60, 0.09), #ffffff 55%, #ffffff 100%);
    clip-path: inherit;
  }

  &:hover {
    transform: translateY(-4px);
    filter: drop-shadow(0 14px 26px rgba(224, 141, 60, 0.18)) brightness(1.05);
  }
`;

const Portrait = styled.div`
  --pcut: 10px;
  position: relative;
  isolation: isolate;
  width: 92px;
  height: 108px;
  flex: none;
  background: linear-gradient(140deg, rgba(242, 176, 94, 0.8), rgba(90, 50, 16, 0.9) 50%, rgba(242, 176, 94, 0.55));
  clip-path: polygon(
    var(--pcut) 0,
    100% 0,
    100% calc(100% - var(--pcut)),
    calc(100% - var(--pcut)) 100%,
    0 100%,
    0 var(--pcut)
  );
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  .initials {
    position: absolute;
    inset: 2px;
    clip-path: inherit;
    width: calc(100% - 4px);
    height: calc(100% - 4px);
    object-fit: cover;
    object-position: center top;
  }

  .initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, #f1ede4, #f4f2ec);
    font-family: var(--font-display);
    font-size: 1.5rem;
    font-weight: 800;
    color: var(--copper);
  }
`;

const MemberInfo = styled.div`
  min-width: 0;

  h3 {
    font-family: var(--font-body);
    font-size: 1.02rem;
    font-weight: 600;
    line-height: 1.3;
  }

  .rule {
    width: 30px;
    height: 2px;
    background: var(--copper);
    margin: 0.5rem 0 0.55rem;
  }

  p {
    color: var(--ink-dim);
    font-size: 0.84rem;
    line-height: 1.55;
  }
`;

const InstBadge = styled.span`
  position: absolute;
  right: 0.95rem;
  bottom: 0.85rem;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  color: var(--copper-bright);
  background: linear-gradient(140deg, rgba(242, 176, 94, 0.7), rgba(110, 60, 18, 0.8));
  clip-path: polygon(50% 0, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%);

  &::before {
    content: '';
    position: absolute;
    inset: 1.5px;
    z-index: -1;
    background: #f4f2ec;
    clip-path: inherit;
  }

  svg {
    position: relative;
  }
`;

const initialsOf = (name) =>
  name
    .replace(/^(Prof\.|Dr\.|Mr\.|Ms\.|Mrs\.)\s*/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

export default function Committee() {
  const [activeId, setActiveId] = useState(slugify(COMMITTEES[0].title));
  const groupRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    Object.values(groupRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <HeroWrap>
        <DotLattice />
        <CopperGlow style={{ top: '-260px', right: '-160px' }} />
        <StructureArt />
        <Container style={{ position: 'relative' }}>
          <Reveal>
            <Kicker index="07">Organizing Committee</Kicker>
            <HeroTitle>
              The people behind the <em>program.</em>
            </HeroTitle>
            <HeroLead>
              {CONFERENCE.shortName} · {CONFERENCE.venueShort} — leadership and committees across both
              organizing institutions.
            </HeroLead>
            <HeroRule aria-hidden="true" />
          </Reveal>
        </Container>
      </HeroWrap>

      <JumpNav aria-label="Jump to committee">
        <Container>
          <div className="rail">
            {COMMITTEES.map((c, i) => (
              <Chip key={c.title} href={`#${slugify(c.title)}`} $active={activeId === slugify(c.title)}>
                {COMMITTEE_ICONS[i % COMMITTEE_ICONS.length]}
                {c.title}
              </Chip>
            ))}
          </div>
        </Container>
      </JumpNav>

      <Section style={{ paddingTop: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
        <Container>
          {COMMITTEES.map((committee, i) => {
            const id = slugify(committee.title);
            return (
              <Group
                key={committee.title}
                id={id}
                ref={(el) => {
                  groupRefs.current[id] = el;
                }}
              >
                <GroupTitle>
                  <Reveal>
                    <span className="idx">/{String(i + 1).padStart(2, '0')}</span>
                    <h2>{committee.title}</h2>
                    <div className="tick" aria-hidden="true" />
                    <div className="dots" aria-hidden="true" />
                  </Reveal>
                </GroupTitle>
                <MemberGrid>
                  {committee.members.map((member, j) => {
                    const photo = COMMITTEE_PHOTOS[member.name];
                    const isIitg = /IIT Guwahati|Nanotechnology/i.test(member.affiliation);
                    return (
                      <Reveal key={`${committee.title}-${member.name}-${j}`} delay={(j % 4) * 60}>
                        <MemberCard>
                          <Portrait>
                            {photo ? (
                              <img
                                src={photo}
                                alt={member.name}
                                loading="lazy"
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  if (e.currentTarget.nextSibling) {
                                    e.currentTarget.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            <span className="initials" style={photo ? { display: 'none' } : undefined}>
                              {initialsOf(member.name)}
                            </span>
                          </Portrait>
                          <MemberInfo>
                            <h3>{member.name}</h3>
                            <div className="rule" aria-hidden="true" />
                            <p>{member.affiliation}</p>
                          </MemberInfo>
                          <InstBadge title={isIitg ? 'IIT Guwahati' : 'IEM / UEM Kolkata'}>
                            <FaUniversity aria-hidden="true" />
                          </InstBadge>
                        </MemberCard>
                      </Reveal>
                    );
                  })}
                </MemberGrid>
              </Group>
            );
          })}
        </Container>
      </Section>
    </>
  );
}
